import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useAdminStatus } from "@/hooks/auth/useAdminStatus";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUpload } from "@/components/ui/FileUpload";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Bot,
  Play,
  Loader2,
  Copy,
  Sparkles,
  FileText,
  Trash2,
  Settings,
  ArrowLeft,
  AlertTriangle,
  Info,
  Share2,
  Edit,
  Calendar,
  User as UserIcon,
  Star,
} from "lucide-react";
import { aiAssistantService } from "@/services/aiAssistantService";
import { Tables } from "@/types/database.types";
import { AIContentRenderer } from "@/components/ai-assistant/AIContentRenderer";
import { EditableAIContentRenderer } from "@/components/ai-assistant/EditableAIContentRenderer";
import { ShareOutputDialog } from "@/components/ai-assistant/ShareOutputDialog";
import { ShareInstanceDialog } from "@/components/ai-assistant/ShareInstanceDialog";
import { RegenerateModal } from "@/components/ai-assistant/RegenerateModal";
import { CopyContentButton } from "@/components/ai-assistant/CopyContentButton";
import { supabase } from "@/lib/supabase";
import { SmartIcon } from "@/components/ui/SmartIcon";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { AIAssistantForm } from "@/components/super-admin/AIAssistantForm";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OutputHeader } from "@/components/ai-assistant/OutputHeader";
import { AIOutputView } from "@/components/ai-assistant/AIOutputView";
import { FolderAssignDropdown } from "@/components/ai-assistant/FolderAssignDropdown";
import { AssistantInputForm } from "@/components/ai-assistant/detail/AssistantInputForm";
import { AssistantGeneratedPanel } from "@/components/ai-assistant/detail/AssistantGeneratedPanel";
import { AssistantResultsList } from "@/components/ai-assistant/detail/AssistantResultsList";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { profileService } from "@/services/profileService";

type AIAssistant = Tables<"ai_assistants">;
type AIAssistantInstance = Tables<"ai_assistant_instances">;
type AIOutput = Tables<"ai_outputs">;

interface FormField {
  label: string;
  type: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  options?: string[];
}

const AIAssistantDetail: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { organization } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // States
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    // Restore form data from localStorage (excludes file data to avoid quota issues)
    if (typeof window !== "undefined" && instanceId) {
      const saved = localStorage.getItem(`ai-assistant-form-${instanceId}`);
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [currentOutputId, setCurrentOutputId] = useState<string | null>(() => {
    // Restore current output ID from localStorage
    if (typeof window !== "undefined" && instanceId) {
      const saved = localStorage.getItem(
        `ai-assistant-output-id-${instanceId}`,
      );
      return saved && saved !== "" ? saved : null;
    }
    return null;
  });
  const [selectedSavedOutput, setSelectedSavedOutput] =
    useState<AIOutput | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    return tabParam === "results" ? "results" : "generate";
  });

  // Check if there's a specific output to show
  const targetOutputId = searchParams.get("output");
  const [resultsFilter, setResultsFilter] = useState<
    "saved" | "all" | "external"
  >("saved");
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isResultsImproving, setIsResultsImproving] = useState(false);
  const [showSuperAdminModal, setShowSuperAdminModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Admin status hook
  const { isAdmin } = useAdminStatus();

  // Settings modal form state
  const [settingsForm, setSettingsForm] = useState({
    title: "",
    description: "",
    custom_instructions: "",
    cta_label: "",
  });

  // Effects for persistence (form data and output ID only)
  useEffect(() => {
    if (typeof window !== "undefined" && instanceId) {
      // Save form data for better UX. For file/image arrays, strip base64 but keep lightweight metadata
      const formDataToSave = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            const first = value[0] as any;
            // Detect our UploadedFile shape and sanitize
            if (first && (first.url || first.base64 || first.name)) {
              const sanitized = value.map((f: any) => ({
                id: f.id,
                name: f.name,
                url: f.url,
                type: f.type,
                size: f.size,
              }));
              return [key, sanitized];
            }
          }
          return [key, value];
        }),
      );

      try {
        localStorage.setItem(
          `ai-assistant-form-${instanceId}`,
          JSON.stringify(formDataToSave),
        );
      } catch (error) {
        console.warn("Failed to save form data to localStorage:", error);
        // If it still fails, clear localStorage to free up space
        try {
          localStorage.removeItem(`ai-assistant-form-${instanceId}`);
        } catch (clearError) {
          console.warn("Failed to clear localStorage:", clearError);
        }
      }

      // Save current output ID to restore on page refresh
      if (currentOutputId) {
        localStorage.setItem(
          `ai-assistant-output-id-${instanceId}`,
          currentOutputId,
        );
      } else {
        localStorage.removeItem(`ai-assistant-output-id-${instanceId}`);
      }
    }
  }, [formData, currentOutputId, instanceId]);

  // Fetch assistant instance data
  const {
    data: instanceData,
    isLoading: instanceLoading,
    error: instanceError,
  } = useQuery({
    queryKey: ["aiAssistantInstance", instanceId],
    queryFn: async () => {
      const result = await aiAssistantService.getAssistantInstance(instanceId!);
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    enabled: !!instanceId,
  });

  // Fetch base assistant data
  const {
    data: baseAssistant,
    isLoading: baseLoading,
    error: baseError,
  } = useQuery({
    queryKey: ["aiAssistant", instanceData?.ai_assistant_id],
    queryFn: async () => {
      const result = await aiAssistantService.getSystemAssistant(
        instanceData!.ai_assistant_id,
      );
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    enabled: !!instanceData?.ai_assistant_id,
  });

  // Fetch system assistants for switcher
  const { data: systemAssistants } = useQuery({
    queryKey: ["systemAssistants"],
    queryFn: async () => {
      const result = await aiAssistantService.getSystemAssistants();
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
  });

  // Fetch saved outputs for this instance
  const { data: savedOutputs, isLoading: savedOutputsLoading } = useQuery({
    queryKey: ["savedOutputs", instanceId],
    queryFn: async () => {
      if (!instanceId) return [];
      const result = await aiAssistantService.getSavedOutputs(instanceId);
      if (result.error) {
        throw result.error;
      }
      let data = result.data || [];
      // Enrich with user names for consistent display
      const userIds = Array.from(
        new Set(data.map((o: any) => o.user_id).filter(Boolean)),
      );
      if (userIds.length > 0) {
        const prof = await profileService.getProfilesByIds(userIds as string[]);
        if (prof.success && prof.data) {
          const idToName = new Map<string, string>();
          prof.data.forEach((p) => {
            const full =
              p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ");
            if (p.id && full) idToName.set(p.id, full);
          });
          data = data.map((o: any) => ({
            ...o,
            user_name: o.user_id ? idToName.get(o.user_id) || "" : "",
          }));
        }
      }
      return data;
    },
    enabled: !!instanceId,
  });

  // Fetch all outputs for this instance
  const { data: allOutputs, isLoading: allOutputsLoading } = useQuery({
    queryKey: ["allOutputs", instanceId],
    queryFn: async () => {
      if (!instanceId) return [];
      const result = await aiAssistantService.getAllOutputs(instanceId);
      if (result.error) {
        throw result.error;
      }
      let data = result.data || [];
      const userIds = Array.from(
        new Set(data.map((o: any) => o.user_id).filter(Boolean)),
      );
      if (userIds.length > 0) {
        const prof = await profileService.getProfilesByIds(userIds as string[]);
        if (prof.success && prof.data) {
          const idToName = new Map<string, string>();
          prof.data.forEach((p) => {
            const full =
              p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ");
            if (p.id && full) idToName.set(p.id, full);
          });
          data = data.map((o: any) => ({
            ...o,
            user_name: o.user_id ? idToName.get(o.user_id) || "" : "",
          }));
        }
      }
      return data;
    },
    enabled: !!instanceId && resultsFilter === "all",
  });

  // Fetch external outputs for this instance
  const { data: externalOutputs, isLoading: externalOutputsLoading } = useQuery(
    {
      queryKey: ["externalOutputs", instanceId],
      queryFn: async () => {
        if (!instanceId) return [];
        const result = await aiAssistantService.getExternalOutputs(instanceId);
        if (result.error) {
          throw result.error;
        }
        const data = result.data || [];
        return data;
      },
      enabled: !!instanceId && resultsFilter === "external",
    },
  );

  // Fetch current output content if we have an outputId
  const { data: currentOutput } = useQuery({
    queryKey: ["currentOutput", currentOutputId],
    queryFn: async () => {
      if (!currentOutputId) return null;
      const result = await aiAssistantService.getOutputById(currentOutputId);
      if (result.error) {
        console.error("Error fetching current output:", result.error);
        return null;
      }
      return result.data;
    },
    enabled: !!currentOutputId,
  });

  // Set generatedContent when currentOutput is loaded
  useEffect(() => {
    if (currentOutput && currentOutput.structured_output !== undefined) {
      const structured: any = currentOutput.structured_output as any;
      const title: string =
        (currentOutput as any)?.output_title || "Generated Content";
      if (typeof structured === "string") {
        setGeneratedContent({ output_title: title, content: structured });
      } else {
        setGeneratedContent(structured);
      }
    }
  }, [currentOutput]);

  // Get current outputs based on filter
  const getCurrentOutputs = () => {
    switch (resultsFilter) {
      case "saved":
        return savedOutputs || [];
      case "all":
        return allOutputs || [];
      case "external":
        return externalOutputs || [];
      default:
        return savedOutputs || [];
    }
  };

  const getCurrentOutputsLoading = () => {
    switch (resultsFilter) {
      case "saved":
        return savedOutputsLoading;
      case "all":
        return allOutputsLoading;
      case "external":
        return externalOutputsLoading;
      default:
        return savedOutputsLoading;
    }
  };

  // Check if there are any public outputs for the green dot indicator
  const hasPublicOutputs = getCurrentOutputs().some(
    (output) => output.is_public,
  );

  // Check if an output is saved
  const isOutputSaved = (outputId: string) => {
    return savedOutputs?.some((output) => output.id === outputId) || false;
  };

  // Initialize settings form when modal opens or instanceData changes
  useEffect(() => {
    if (isSettingsModalOpen && instanceData && baseAssistant) {
      setSettingsForm({
        title: instanceData.title || baseAssistant.title || "",
        description:
          instanceData.description || baseAssistant.description || "",
        custom_instructions: instanceData.custom_instructions || "",
        cta_label:
          instanceData.cta_label || baseAssistant.cta_label || "Generate",
      });
    }
  }, [isSettingsModalOpen, instanceData, baseAssistant]);

  // Mutation for updating assistant instance
  const updateInstanceMutation = useMutation({
    mutationFn: async (updates: typeof settingsForm) => {
      if (!instanceId) throw new Error("No instance ID");
      const result = await aiAssistantService.updateAssistantInstance(
        instanceId,
        updates,
      );
      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["aiAssistantInstance", instanceId],
      });
      setIsSettingsModalOpen(false);
      toast({
        title: "Settings saved",
        description: "Assistant settings have been updated.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to save settings",
      });
    },
  });

  // Mutation for saving an output
  const saveOutputMutation = useMutation({
    mutationFn: async (outputId: string) => {
      const result = await aiAssistantService.saveAIOutput(outputId);
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedOutputs", instanceId] });
      queryClient.invalidateQueries({ queryKey: ["allOutputs", instanceId] });
      toast({
        title: "Starred",
        description: "Output has been added to your starred items.",
      });
    },
    onError: (error) => {
      toast({
        title: "Star Failed",
        description:
          error instanceof Error ? error.message : "Failed to star output.",
        variant: "destructive",
      });
    },
  });

  // Mutation for deleting a saved output
  const deleteSavedOutputMutation = useMutation({
    mutationFn: async (outputId: string) => {
      const result = await aiAssistantService.deleteSavedOutput(outputId);
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedOutputs", instanceId] });
      queryClient.invalidateQueries({ queryKey: ["allOutputs", instanceId] });
      queryClient.invalidateQueries({
        queryKey: ["externalOutputs", instanceId],
      });
      toast({
        title: "Removed",
        description: "Generation has been removed from your saved collection.",
      });
    },
    onError: (error) => {
      toast({
        title: "Remove Failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to remove saved generation.",
        variant: "destructive",
      });
    },
  });

  // Mutation for updating AI output content (inline editing)
  const updateContentMutation = useMutation({
    mutationFn: async ({
      outputId,
      newContent,
    }: {
      outputId: string;
      newContent: string | Record<string, any>;
    }) => {
      const result = await aiAssistantService.updateAIOutputContent(
        outputId,
        newContent,
      );

      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: (updatedOutput) => {
      // Refresh saved outputs to reflect changes
      queryClient.invalidateQueries({ queryKey: ["savedOutputs", instanceId] });

      // Update local state if this is the current generated content
      if (updatedOutput && currentOutputId === updatedOutput.id) {
        setGeneratedContent(updatedOutput.structured_output);
      }

      // Update selected saved output if applicable
      if (
        selectedSavedOutput &&
        updatedOutput &&
        selectedSavedOutput.id === updatedOutput.id
      ) {
        setSelectedSavedOutput(updatedOutput as any);
      }
    },
    onError: (error) => {
      console.error("Error updating content:", error);
      throw error; // Re-throw so EditableAIContentRenderer can handle it
    },
  });

  // Mutation for updating system assistant (Super Admin only)
  const updateSystemAssistantMutation = useMutation({
    mutationFn: async (updates: any) => {
      if (!baseAssistant) {
        throw new Error("No assistant to update");
      }
      const result = await aiAssistantService.updateSystemAssistant(
        baseAssistant.id,
        updates,
      );
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate all related queries to ensure UI updates
      queryClient.invalidateQueries({
        queryKey: ["aiAssistantInstance", instanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["aiAssistant", instanceData?.ai_assistant_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["systemAssistants"],
      });

      setShowSuperAdminModal(false);
      toast({
        title: "Success",
        description: "Assistant updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Save handlers for editable content
  const handleSaveGeneratedContent = async (
    newContent: string | Record<string, any>,
  ) => {
    if (!currentOutputId) {
      throw new Error("No output ID available for saving");
    }

    await updateContentMutation.mutateAsync({
      outputId: currentOutputId,
      newContent,
    });
  };

  const handleSaveSavedContent = async (
    newContent: string | Record<string, any>,
  ) => {
    if (!selectedSavedOutput) {
      throw new Error("No saved output selected");
    }

    await updateContentMutation.mutateAsync({
      outputId: selectedSavedOutput.id,
      newContent,
    });
  };

  // Parse form fields
  const formFields: FormField[] = useMemo(() => {
    if (!instanceData && !baseAssistant) return [];
    try {
      const fields =
        (instanceData?.custom_form_fields as any) ||
        (baseAssistant?.form_fields as any);
      return Array.isArray(fields) ? (fields as FormField[]) : [];
    } catch (error) {
      console.error("Error parsing form fields:", error);
      return [];
    }
  }, [instanceData?.custom_form_fields, baseAssistant?.form_fields]);

  // Form field change handler
  const handleFormFieldChange = (label: string, value: string | any[]) => {
    setFormData((prev) => ({
      ...prev,
      [label]: value,
    }));
  };

  // Determine the JSON schema to guide rendering order
  const renderingSchema = useMemo(() => {
    try {
      return instanceData?.custom_zod_schema || baseAssistant?.zod_schema;
    } catch {
      return undefined;
    }
  }, [instanceData?.custom_zod_schema, baseAssistant?.zod_schema]);

  // Load saved output into content area
  const handleLoadSavedOutput = (savedOutput: AIOutput) => {
    const structured: any = savedOutput.structured_output as any;
    const title: string = (savedOutput as any)?.output_title || "Saved Content";
    if (typeof structured === "string") {
      setGeneratedContent({ output_title: title, content: structured });
    } else {
      setGeneratedContent(structured);
    }
    setCurrentOutputId(savedOutput.id);
    setSelectedSavedOutput(savedOutput);
    setGenerationError(null);

    toast({
      title: "Loaded",
      description: "Starred output has been loaded.",
    });
  };

  // Save/unsave current generation
  const handleSaveGeneration = () => {
    if (!currentOutputId) {
      toast({
        title: "Nothing to Save",
        description: "Please generate content first.",
        variant: "destructive",
      });
      return;
    }

    if (isCurrentOutputSaved) {
      // If already saved, unsave it
      deleteSavedOutputMutation.mutate(currentOutputId);
    } else {
      // If not saved, save it
      saveOutputMutation.mutate(currentOutputId);
    }
  };

  // Regenerate handler for Results tab (selectedSavedOutput)
  const handleRegenerateResults = async (feedback?: string) => {
    if (!selectedSavedOutput || !instanceData || !baseAssistant) return;
    try {
      setIsResultsImproving(true);
      const organizationId = organization?.id || instanceData.organization_id;
      const assistantId = selectedSavedOutput.ai_assistant_id as string;
      const instanceId = (selectedSavedOutput as any)
        .ai_assistant_instance_id as string | null;

      const assistantConfig: any = {
        baseInstructions:
          (instanceData as any).custom_instructions ||
          (baseAssistant as any).base_instructions,
        customInstructions: (instanceData as any).custom_instructions,
        zodSchema:
          (instanceData as any).custom_zod_schema ||
          (baseAssistant as any).zod_schema,
        formFields:
          (instanceData as any).custom_form_fields ||
          (baseAssistant as any).form_fields,
        model: (baseAssistant as any).model,
        temperature: (baseAssistant as any).temperature,
        max_output_tokens: (baseAssistant as any).max_output_tokens,
        web_search_enabled: (baseAssistant as any).web_search_enabled,
        generation_mode: (baseAssistant as any).generation_mode || "object",
      };

      const url = `${(window as any).ENV?.VITE_SUPABASE_URL || (import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`;
      const reqBody: any = {
        assistantId,
        instanceId,
        inputData: (selectedSavedOutput as any).input_data || {},
        organizationId,
        userId: user?.id,
        assistantConfig,
        isRegeneration: true,
        previousOutput: (selectedSavedOutput as any).structured_output,
        userFeedback: feedback,
        outputId: selectedSavedOutput.id,
      };

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(window as any).ENV?.VITE_SUPABASE_ANON_KEY || (import.meta as any).env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(reqBody),
      });

      const reader = resp.body?.getReader();
      if (!reader) throw new Error("Failed to open stream");
      const decoder = new TextDecoder();
      let buffer = "";
      let textBuffer = "";

      // Wipe content before streaming
      setSelectedSavedOutput((prev) =>
        prev ? { ...prev, structured_output: "" } : prev,
      );

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "text-delta") {
              const delta: string = data.data || "";
              textBuffer += delta;
              setSelectedSavedOutput((prev) =>
                prev ? { ...prev, structured_output: textBuffer } : prev,
              );
            }
            if (data.type === "partial") {
              const part = data.data;
              setSelectedSavedOutput((prev) =>
                prev ? { ...prev, structured_output: part } : prev,
              );
            }
            if (data.type === "complete") {
              const finalData = data.data;
              setSelectedSavedOutput((prev) =>
                prev ? { ...prev, structured_output: finalData } : prev,
              );
            }
          } catch (_) {}
        }
      }
    } catch (e: any) {
      toast({
        title: "Regeneration failed",
        description: e?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsResultsImproving(false);
    }
  };

  // Check if current output is already saved
  const isCurrentOutputSaved =
    currentOutputId &&
    savedOutputs?.some((output) => output.id === currentOutputId);

  // Generation handler
  const handleStartGeneration = async () => {
    if (!instanceData || !baseAssistant) {
      toast({
        title: "Error",
        description: "Missing required data for generation.",
        variant: "destructive",
      });
      return;
    }

    // Use organization_id from instanceData if organization hook fails
    const organizationId = organization?.id || instanceData.organization_id;

    if (!organizationId) {
      toast({
        title: "Error",
        description: "Organization information not available.",
        variant: "destructive",
      });
      return;
    }

    // Validate required form fields (support arrays for file/image inputs)
    const requiredFields = formFields.filter((field) => field.required);
    const missingFields = requiredFields.filter((field) => {
      const value = formData[field.label];
      if (field.type === "file" || field.type === "image") {
        return !Array.isArray(value) || value.length === 0;
      }
      if (value === undefined || value === null) return true;
      if (typeof value === "string") return value.trim() === "";
      // For other types (numbers, booleans, objects), treat as provided
      return false;
    });

    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill in: ${missingFields.map((f) => f.label).join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setGeneratedContent(null);
    setCurrentOutputId(null);
    setSelectedSavedOutput(null);

    try {
      // Prepare assistant configuration
      const assistantConfig = {
        baseInstructions:
          instanceData.custom_instructions || baseAssistant.base_instructions,
        customInstructions: instanceData.custom_instructions,
        zodSchema: instanceData.custom_zod_schema || baseAssistant.zod_schema,
        formFields:
          instanceData.custom_form_fields || baseAssistant.form_fields,
        // Ensure model and related options are sent to the Edge Function
        model: baseAssistant.model,
        temperature: baseAssistant.temperature,
        max_output_tokens: baseAssistant.max_output_tokens,
        web_search_enabled: baseAssistant.web_search_enabled,
        generation_mode: (baseAssistant as any).generation_mode || "object",
      };

      // Create request body
      const requestBody = {
        assistantId: baseAssistant.id,
        instanceId: instanceData.id,
        inputData: formData,
        organizationId: organizationId,
        userId: user?.id, // Include current user ID
        assistantConfig,
      };

      // Start streaming generation
      const response = await fetch(
        `${(window as any).ENV?.VITE_SUPABASE_URL || (import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(window as any).ENV?.VITE_SUPABASE_ANON_KEY || (import.meta as any).env?.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`Generation failed: ${response.statusText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      let buffer = "";
      let streamMode: "object" | "text" = "object";
      let textAccumulator = "";
      let metaTitle: string | null = null;

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));

                switch (data.type) {
                  case "meta":
                    streamMode = data.mode === "text" ? "text" : "object";
                    if (
                      data.outputTitle &&
                      typeof data.outputTitle === "string"
                    ) {
                      metaTitle = data.outputTitle;
                      setGeneratedContent((prev: any) => ({
                        ...(typeof prev === "object" && prev !== null
                          ? prev
                          : {}),
                        output_title: metaTitle,
                      }));
                    }
                    break;
                  case "text-delta":
                    // Accumulate and live render text in text mode
                    textAccumulator += data.data || "";
                    if (streamMode === "text") {
                      setGeneratedContent({
                        output_title: metaTitle || "Generated Content",
                        content: textAccumulator,
                      });
                    }
                    break;
                  case "partial":
                    // Update with partial content
                    console.log(
                      "🔄 Streaming partial:",
                      typeof data.data,
                      data.data,
                    );

                    // Debug nested objects in streaming
                    if (typeof data.data === "object" && data.data !== null) {
                      Object.entries(data.data).forEach(([key, value]) => {
                        if (
                          typeof value === "object" &&
                          value !== null &&
                          !Array.isArray(value)
                        ) {
                          console.log(
                            `🔄 Streaming nested object in "${key}":`,
                            typeof value,
                            Object.entries(value),
                          );
                        }
                      });
                    }

                    // Preserve title when updating partial content
                    if (streamMode === "text") {
                      setGeneratedContent({
                        output_title:
                          metaTitle ||
                          (typeof (generatedContent as any)?.output_title ===
                          "string"
                            ? (generatedContent as any).output_title
                            : "Generated Content"),
                        content: textAccumulator,
                      });
                    } else {
                      setGeneratedContent((prev: any) => {
                        const preservedTitle =
                          typeof prev === "object" &&
                          prev !== null &&
                          prev.output_title
                            ? prev.output_title
                            : metaTitle || "Generated Content";

                        if (
                          typeof data.data === "object" &&
                          data.data !== null
                        ) {
                          return {
                            ...data.data,
                            output_title:
                              data.data.output_title || preservedTitle,
                          };
                        }

                        return data.data;
                      });
                    }
                    break;

                  case "complete":
                    // Set final content and capture output ID
                    console.log(
                      "✅ Generation complete, outputId:",
                      data.outputId,
                    );
                    console.log(
                      "✅ Final content:",
                      typeof data.data,
                      data.data,
                    );

                    // Debug nested objects in final content
                    if (typeof data.data === "object" && data.data !== null) {
                      Object.entries(data.data).forEach(([key, value]) => {
                        if (
                          typeof value === "object" &&
                          value !== null &&
                          !Array.isArray(value)
                        ) {
                          console.log(
                            `✅ Final nested object in "${key}":`,
                            typeof value,
                            Object.entries(value),
                          );
                        }
                      });
                    }

                    if (streamMode === "text") {
                      const finalText =
                        typeof data.data === "string"
                          ? data.data
                          : textAccumulator;
                      if (metaTitle) {
                        setGeneratedContent({
                          output_title: metaTitle,
                          content: finalText,
                        });
                      } else if (
                        data.outputTitle &&
                        typeof data.outputTitle === "string"
                      ) {
                        setGeneratedContent({
                          output_title: data.outputTitle,
                          content: finalText,
                        });
                      } else {
                        setGeneratedContent({
                          output_title: "Generated Content",
                          content: finalText,
                        });
                      }
                    } else {
                      // Preserve/override title on non-text final payloads
                      setGeneratedContent((prev: any) => {
                        const preservedTitle =
                          typeof prev === "object" &&
                          prev !== null &&
                          typeof prev.output_title === "string"
                            ? prev.output_title
                            : metaTitle || "Generated Content";
                        const incomingTitle =
                          data &&
                          data.data &&
                          typeof (data.data as any).output_title === "string"
                            ? ((data.data as any).output_title as string)
                            : "";
                        const selectedTitle =
                          incomingTitle && incomingTitle !== "Generated Content"
                            ? incomingTitle
                            : preservedTitle;
                        if (
                          typeof data.data === "object" &&
                          data.data !== null
                        ) {
                          return {
                            ...(data.data as any),
                            output_title: selectedTitle,
                          };
                        }
                        return {
                          output_title: selectedTitle,
                          content: String(data.data ?? ""),
                        };
                      });
                    }

                    if (data.outputId) {
                      setCurrentOutputId(data.outputId);
                    } else {
                      // Handle missing outputId - this indicates a save failure
                      const errorMessage =
                        data.saveError || "Failed to save generated content";
                      console.error(
                        "💥 Generation completed but outputId missing:",
                        errorMessage,
                      );
                      toast({
                        title: "Star Failed",
                        description: `Content generated but star action failed: ${errorMessage}`,
                        variant: "destructive",
                      });

                      // Reset generating state since we can't properly save
                      setIsGenerating(false);
                      return; // Don't show success toast
                    }

                    toast({
                      title: "Generation Complete!",
                      description:
                        "Your content has been generated successfully.",
                    });
                    break;

                  case "error":
                    throw new Error(data.error);
                }
              } catch (parseError) {
                console.error("Error parsing streaming data:", parseError);
              }
            }
          }
        }
      } finally {
        // Ensure reader is always released
        try {
          reader.releaseLock();
        } catch (lockError) {
          console.warn("Failed to release reader lock:", lockError);
        }
      }
    } catch (error) {
      console.error("Generation error:", error);
      setGenerationError(
        error instanceof Error ? error.message : "Unknown error occurred",
      );
      toast({
        title: "Generation Failed",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Regeneration handler
  const handleRegenerate = async (feedback?: string) => {
    if (
      !instanceData ||
      !baseAssistant ||
      !generatedContent ||
      !currentOutputId
    ) {
      toast({
        title: "Error",
        description: "Missing required data for regeneration.",
        variant: "destructive",
      });
      return;
    }

    const organizationId = organization?.id || instanceData.organization_id;

    if (!organizationId) {
      toast({
        title: "Error",
        description: "Organization information not available.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    // Clear current output so the new stream is visible immediately
    setGeneratedContent("");

    try {
      // Prepare assistant configuration
      const assistantConfig = {
        baseInstructions:
          instanceData.custom_instructions || baseAssistant.base_instructions,
        customInstructions: instanceData.custom_instructions,
        zodSchema: instanceData.custom_zod_schema || baseAssistant.zod_schema,
        formFields:
          instanceData.custom_form_fields || baseAssistant.form_fields,
        model: baseAssistant.model,
        temperature: baseAssistant.temperature,
        maxOutputTokens: baseAssistant.max_output_tokens,
        web_search_enabled: baseAssistant.web_search_enabled,
        generation_mode: (baseAssistant as any).generation_mode || "object",
      };

      // Create request body
      const requestBody = {
        assistantId: baseAssistant.id,
        instanceId: instanceData.id,
        inputData: formData,
        organizationId: organizationId,
        userId: user?.id, // Include current user ID
        assistantConfig,
        // NEW: Regeneration fields
        isRegeneration: true,
        previousOutput: generatedContent,
        userFeedback: feedback,
        outputId: currentOutputId,
      };

      // Start streaming regeneration
      const response = await fetch(
        `${(window as any).ENV?.VITE_SUPABASE_URL || (import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(window as any).ENV?.VITE_SUPABASE_ANON_KEY || (import.meta as any).env?.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`Regeneration failed: ${response.statusText}`);
      }

      // Handle streaming response (align with handleStartGeneration): support text deltas
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      let buffer = "";
      let streamMode: "object" | "text" = "object";
      let textAccumulator = "";
      let metaTitle: string | null = null;

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));

                switch (data.type) {
                  case "meta":
                    streamMode = data.mode === "text" ? "text" : "object";
                    if (
                      data.outputTitle &&
                      typeof data.outputTitle === "string"
                    ) {
                      metaTitle = data.outputTitle;
                      setGeneratedContent((prev: any) => ({
                        ...(typeof prev === "object" && prev !== null
                          ? prev
                          : {}),
                        output_title: metaTitle,
                      }));
                    }
                    break;

                  case "text-delta":
                    textAccumulator += data.data || "";
                    if (streamMode === "text") {
                      setGeneratedContent({
                        output_title: metaTitle || "Generated Content",
                        content: textAccumulator,
                      });
                    }
                    break;

                  case "partial":
                    // Preserve title when updating partial content during regeneration
                    if (streamMode === "text") {
                      setGeneratedContent({
                        output_title:
                          metaTitle ||
                          (typeof (generatedContent as any)?.output_title ===
                          "string"
                            ? (generatedContent as any).output_title
                            : "Generated Content"),
                        content: textAccumulator,
                      });
                    } else {
                      setGeneratedContent((prev: any) => {
                        const preservedTitle =
                          typeof prev === "object" &&
                          prev !== null &&
                          prev.output_title
                            ? prev.output_title
                            : metaTitle || "Generated Content";

                        if (
                          typeof data.data === "object" &&
                          data.data !== null
                        ) {
                          return {
                            ...data.data,
                            output_title:
                              data.data.output_title || preservedTitle,
                          };
                        }

                        return data.data;
                      });
                    }
                    break;

                  case "complete":
                    if (streamMode === "text") {
                      const finalText =
                        typeof data.data === "string"
                          ? data.data
                          : textAccumulator;
                      if (metaTitle) {
                        setGeneratedContent({
                          output_title: metaTitle,
                          content: finalText,
                        });
                      } else if (
                        data.outputTitle &&
                        typeof data.outputTitle === "string"
                      ) {
                        setGeneratedContent({
                          output_title: data.outputTitle,
                          content: finalText,
                        });
                      } else {
                        setGeneratedContent({
                          output_title: "Generated Content",
                          content: finalText,
                        });
                      }
                    } else {
                      // Preserve/override title on non-text final payloads (regeneration)
                      setGeneratedContent((prev: any) => {
                        const preservedTitle =
                          typeof prev === "object" &&
                          prev !== null &&
                          typeof prev.output_title === "string"
                            ? prev.output_title
                            : metaTitle || "Generated Content";
                        const incomingTitle =
                          data &&
                          data.data &&
                          typeof (data.data as any).output_title === "string"
                            ? ((data.data as any).output_title as string)
                            : "";
                        const selectedTitle =
                          incomingTitle && incomingTitle !== "Generated Content"
                            ? incomingTitle
                            : preservedTitle;
                        if (
                          typeof data.data === "object" &&
                          data.data !== null
                        ) {
                          return {
                            ...(data.data as any),
                            output_title: selectedTitle,
                          };
                        }
                        return {
                          output_title: selectedTitle,
                          content: String(data.data ?? ""),
                        };
                      });
                    }
                    if (data.outputId) {
                      setCurrentOutputId(data.outputId);
                    }
                    toast({
                      title: "Regeneration Complete!",
                      description:
                        "Your content has been regenerated successfully.",
                    });
                    break;

                  case "error":
                    throw new Error(data.error);
                }
              } catch (parseError) {
                console.error("Error parsing streaming data:", parseError);
              }
            }
          }
        }
      } finally {
        // Ensure reader is always released
        try {
          reader.releaseLock();
        } catch (lockError) {
          console.warn("Failed to release reader lock:", lockError);
        }
      }
    } catch (error: any) {
      console.error("Regeneration error:", error);
      setGenerationError(error.message);
      toast({
        title: "Regeneration Failed",
        description: error.message || "Failed to regenerate content.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Render form field moved into AssistantInputForm

  // Loading states
  if (instanceLoading || baseLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading assistant...</p>
        </div>
      </div>
    );
  }

  if (instanceError || baseError || !instanceData || !baseAssistant) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Assistant Not Found</h3>
          <p className="text-muted-foreground mb-4">
            The requested AI assistant could not be found. This might happen if
            you're accessing the page directly.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            To use an AI assistant, please go to the Agents page and select an
            assistant to get started.
          </p>
          <Button onClick={() => navigate("/agents")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go to Agents
          </Button>

          {/* Debug info (remove in production) */}
          <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left text-xs">
            <h4 className="font-semibold mb-2">Debug Info:</h4>
            <div>Instance ID: {instanceId}</div>
            <div>Instance Error: {instanceError?.message}</div>
            <div>Base Error: {baseError?.message}</div>
            <div>Instance Data: {instanceData ? "✅" : "❌"}</div>
            <div>Base Assistant: {baseAssistant ? "✅" : "❌"}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout>
      {/* Fixed Top Navbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/ai-assistants")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div className="h-6 w-px bg-gray-300" />

          <Select
            value={baseAssistant.id}
            onValueChange={async (newAssistantId) => {
              // Guard against empty value events from the wrapper Select
              if (!newAssistantId) return;
              const orgId = organization?.id || instanceData.organization_id;
              if (!orgId) return;
              try {
                const { data: newInstance } =
                  await aiAssistantService.findOrCreateAssistantInstance(
                    newAssistantId,
                    orgId,
                  );
                if (newInstance) navigate(`/ai-assistants/${newInstance.id}`);
              } catch (err) {
                console.warn(
                  "Non-blocking switcher onValueChange warning:",
                  err,
                );
              }
            }}
          >
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[320px] justify-between">
                  <span className="truncate max-w-[240px] block">
                    {baseAssistant?.title || "Select assistant"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[280px]">
                <Command>
                  <CommandInput placeholder="Search assistants..." />
                  <CommandList>
                    <CommandEmpty>No assistants found.</CommandEmpty>
                    <CommandGroup>
                      {systemAssistants?.map((assistant) => (
                        <CommandItem
                          key={assistant.id}
                          value={assistant.title}
                          onSelect={async () => {
                            try {
                              const orgId =
                                organization?.id ||
                                instanceData.organization_id;
                              const { data: newInstance, error } =
                                await aiAssistantService.findOrCreateAssistantInstance(
                                  assistant.id,
                                  orgId,
                                  assistant.name,
                                );
                              if (error || !newInstance) {
                                toast({
                                  title: "Assistant not available",
                                  description:
                                    error?.message ||
                                    "Could not access this assistant for your organization.",
                                  variant: "destructive",
                                });
                                return;
                              }
                              navigate(`/ai-assistants/${newInstance.id}`);
                            } catch (e: any) {
                              toast({
                                title: "Error",
                                description:
                                  e?.message || "Failed to switch assistant",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <span className="truncate max-w-[220px] block">
                            {assistant.title}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Select>
        </div>

        {/* Centered Tabs */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <Tabs
            value={activeTab}
            onValueChange={(val) => {
              setActiveTab(val);
              const url = new URL(window.location.href);
              url.searchParams.set("tab", val);
              window.history.replaceState({}, "", url.toString());
            }}
            className="w-auto"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">Generate</TabsTrigger>
              <TabsTrigger value="results">
                Results
                {savedOutputs && savedOutputs.length > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                    {savedOutputs.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center gap-2">
          {/* Super Admin Edit Button */}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuperAdminModal(true)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit AI tool
            </Button>
          )}

          {/* Org-owned Assistant Edit (when not super admin) */}
          {!isAdmin &&
            baseAssistant &&
            instanceData &&
            (baseAssistant as any).owner_organization_id &&
            instanceData.organization_id ===
              (baseAssistant as any).owner_organization_id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSuperAdminModal(true)}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit AI tool
              </Button>
            )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSettingsModalOpen(true)}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Settings
          </Button>

          {/* Share Assistant (instance) */}
          {instanceData?.id && (
            <ShareInstanceDialog instanceId={instanceData.id}>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2"
              >
                <Share2 className="h-4 w-4" /> Share AI tool
              </Button>
            </ShareInstanceDialog>
          )}

          {/* Removed top-right Share Output button; moved to content toolbar */}
          {/* If we still have an output selected, show green indicator */}
          {currentOutputId && hasPublicOutputs ? (
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          ) : null}
          {false && (
            // Legacy output-only share button kept for reference
            <ShareOutputDialog outputId={currentOutputId!}>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            </ShareOutputDialog>
          )}
        </div>
      </div>

      {/* Main Content with proper height */}
      <div className="flex-1 flex overflow-hidden">
        {activeTab === "generate" ? (
          <>
            {/* Column 1: Assistant Info & Form Fields */}
            <AssistantInputForm
              bannerUrl={baseAssistant?.banner || null}
              iconUrl={
                instanceData?.icon_url || baseAssistant?.icon_url || null
              }
              title={instanceData?.title || baseAssistant?.title || null}
              description={
                instanceData?.description || baseAssistant?.description || null
              }
              formFields={formFields}
              formData={formData}
              onFieldChange={handleFormFieldChange}
              onGenerate={handleStartGeneration}
              isGenerating={isGenerating}
            />

            {/* Column 2: Generated Content with light grey background */}
            <AssistantGeneratedPanel
              organizationBrandColor={organization?.brand_color}
              formFields={formFields}
              formData={formData}
              generatedContent={generatedContent}
              isGenerating={isGenerating}
              currentOutputId={currentOutputId}
              isCurrentOutputSaved={!!isCurrentOutputSaved}
              renderingSchema={renderingSchema}
              onClickImprove={() => setShowRegenerateModal(true)}
              onClickSave={handleSaveGeneration}
              onTitleSave={async (newTitle: string) => {
                if (!currentOutputId) return;
                setGeneratedContent((prev: any) => {
                  if (prev && typeof prev === "object") {
                    return { ...prev, output_title: newTitle };
                  }
                  return prev;
                });
                await supabase
                  .from("ai_outputs")
                  .update({ output_title: newTitle } as any)
                  .eq("id", currentOutputId);
                queryClient.invalidateQueries({
                  queryKey: ["currentOutput", currentOutputId],
                });
              }}
              onSaveGeneratedContent={handleSaveGeneratedContent}
              generationError={generationError}
              // Keep editor active after refresh when DB flag set on current output
              alwaysEdit={Boolean((currentOutput as any)?.is_edit_mode)}
              extraActionsRight={
                currentOutputId ? (
                  <FolderAssignDropdown
                    outputId={currentOutputId}
                    currentFolderId={undefined}
                    onChange={() => {
                      // noop; list invalidations happen elsewhere
                    }}
                  />
                ) : null
              }
            />
          </>
        ) : (
          /* Results Tab */
          <div className="flex-1 flex">
            {/* Left Column: Results List */}
            <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
              {/* Filter Tabs */}
              <div className="p-4 border-b border-gray-200">
                <Tabs
                  value={resultsFilter}
                  onValueChange={(v) =>
                    setResultsFilter(v as "saved" | "all" | "external")
                  }
                >
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="saved">Starred</TabsTrigger>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="external">External</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Results List Content */}
              <div className="p-4">
                <div className="mb-3">
                  <Input
                    placeholder="Search outputs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {getCurrentOutputsLoading() ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 text-gray-400 mx-auto mb-2 animate-spin" />
                    <p className="text-xs text-gray-500">Loading results...</p>
                  </div>
                ) : getCurrentOutputs().length > 0 ? (
                  <div className="space-y-2">
                    {getCurrentOutputs()
                      .filter((o) => {
                        const input = JSON.stringify(
                          o.input_data || {},
                        ).toLowerCase();
                        const output = JSON.stringify(
                          o.structured_output || (o as any).output_data || {},
                        ).toLowerCase();
                        const term = searchTerm.toLowerCase();
                        return input.includes(term) || output.includes(term);
                      })
                      .map((output) => (
                        <div
                          key={output.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            (selectedSavedOutput?.id || null) === output.id
                              ? "bg-yellow-50 border-yellow-200"
                              : "bg-white border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => handleLoadSavedOutput(output)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-gray-500 flex items-center gap-3">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(output.created_at).toLocaleDateString(
                                  undefined,
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              </span>
                              {!!(output as any).user_id && (
                                <span className="inline-flex items-center gap-1">
                                  <UserIcon className="h-3 w-3" />
                                  {(output as any).user_name || "User"}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const confirmed = window.confirm(
                                    "Are you sure you want to delete this output?",
                                  );
                                  if (confirmed) {
                                    aiAssistantService
                                      .deleteAIOutput(output.id)
                                      .then((res) => {
                                        if (res.error) throw res.error;
                                        queryClient.invalidateQueries({
                                          queryKey: [
                                            "savedOutputs",
                                            instanceId,
                                          ],
                                        });
                                        queryClient.invalidateQueries({
                                          queryKey: ["allOutputs", instanceId],
                                        });
                                        queryClient.invalidateQueries({
                                          queryKey: [
                                            "externalOutputs",
                                            instanceId,
                                          ],
                                        });
                                        if (
                                          selectedSavedOutput?.id === output.id
                                        )
                                          setSelectedSavedOutput(null);
                                      })
                                      .catch((err) => {
                                        console.error("Delete failed", err);
                                        toast({
                                          title: "Delete Failed",
                                          description:
                                            err?.message ||
                                            "Could not delete output",
                                          variant: "destructive",
                                        });
                                      });
                                  }
                                }}
                                className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>

                          <div className="text-xs text-gray-700">
                            {typeof output.input_data === "object" &&
                              output.input_data &&
                              Object.entries(
                                output.input_data as Record<string, any>,
                              )
                                .slice(0, 2)
                                .map(([key, value]) => (
                                  <div key={key} className="truncate">
                                    <span className="font-medium">{key}:</span>{" "}
                                    {String(value).slice(0, 30)}
                                    {String(value).length > 30 && "..."}
                                  </div>
                                ))}
                          </div>

                          {isOutputSaved(output.id) && (
                            <div className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">
                              <Star className="h-3 w-3 fill-current" />
                              <span>Starred</span>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-xs text-gray-500">
                    No results found
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Selected Generated Content with matching styling */}
            <div className="flex-1 bg-gray-50 overflow-y-auto">
              <div className="p-6">
                <div className="max-w-[750px] mx-auto">
                  {selectedSavedOutput ? (
                    <AIOutputView
                      output={selectedSavedOutput}
                      isSaved={isOutputSaved(selectedSavedOutput.id)}
                      onToggleSave={() => {
                        if (isOutputSaved(selectedSavedOutput.id)) {
                          deleteSavedOutputMutation.mutate(
                            selectedSavedOutput.id,
                          );
                        } else {
                          saveOutputMutation.mutate(selectedSavedOutput.id);
                        }
                      }}
                      renderingSchema={renderingSchema as any}
                      isGenerating={isResultsImproving}
                      onImprove={() => setShowRegenerateModal(true)}
                      onTitleSave={async (newTitle: string) => {
                        const res =
                          await aiAssistantService.updateAIOutputTitle(
                            selectedSavedOutput.id,
                            newTitle,
                          );
                        if (!res.error) {
                          setSelectedSavedOutput((prev) =>
                            prev ? { ...prev, output_title: newTitle } : prev,
                          );
                        }
                      }}
                      extraActionsRight={
                        <FolderAssignDropdown
                          outputId={(selectedSavedOutput as any).id}
                          currentFolderId={
                            (selectedSavedOutput as any).folder_id || null
                          }
                          onChange={(folderId) => {
                            setSelectedSavedOutput((prev) =>
                              prev
                                ? { ...prev, folder_id: folderId as any }
                                : prev,
                            );
                          }}
                        />
                      }
                      onContentSaved={(updated) => {
                        setSelectedSavedOutput(updated as any);
                        // Update cached lists so re-selecting shows fresh content without full refresh
                        try {
                          queryClient.setQueryData(
                            ["savedOutputs", instanceId],
                            (old: any) => {
                              if (!old) return old;
                              return (old as any[]).map((o) =>
                                o.id === (updated as any).id
                                  ? { ...o, ...updated }
                                  : o,
                              );
                            },
                          );
                          queryClient.setQueryData(
                            ["allOutputs", instanceId],
                            (old: any) => {
                              if (!old) return old;
                              return (old as any[]).map((o) =>
                                o.id === (updated as any).id
                                  ? { ...o, ...updated }
                                  : o,
                              );
                            },
                          );
                          queryClient.setQueryData(
                            ["externalOutputs", instanceId],
                            (old: any) => {
                              if (!old) return old;
                              return (old as any[]).map((o) =>
                                o.id === (updated as any).id
                                  ? { ...o, ...updated }
                                  : o,
                              );
                            },
                          );
                        } catch (e) {
                          console.warn(
                            "Non-blocking: failed to update cached outputs",
                            e,
                          );
                        }
                      }}
                    />
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Star className="h-6 w-6 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Select a Result
                      </h3>
                      <p className="text-gray-500">
                        Choose a saved generation from the left to view its
                        content.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <Dialog open={isSettingsModalOpen} onOpenChange={setIsSettingsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assistant Settings</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={settingsForm.title}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }));
                }}
                placeholder={
                  baseAssistant?.title || "Custom title for this assistant"
                }
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={settingsForm.description}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                }}
                placeholder={
                  baseAssistant?.description ||
                  "Custom description for this assistant"
                }
                rows={3}
              />
            </div>

            {/* Custom Instructions */}
            <div className="space-y-2">
              <Label htmlFor="custom-instructions">Custom Instructions</Label>
              <Textarea
                id="custom-instructions"
                value={settingsForm.custom_instructions}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    custom_instructions: e.target.value,
                  }));
                }}
                placeholder="Additional instructions for this assistant..."
                rows={4}
              />
            </div>

            {/* CTA Label */}
            <div className="space-y-2">
              <Label htmlFor="cta-label">Button Label</Label>
              <Input
                id="cta-label"
                value={settingsForm.cta_label}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    cta_label: e.target.value,
                  }));
                }}
                placeholder={
                  baseAssistant?.cta_label || "Text for the generation button"
                }
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsSettingsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => updateInstanceMutation.mutate(settingsForm)}
                disabled={updateInstanceMutation.isPending}
              >
                {updateInstanceMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assistant Edit Modal (Super Admin or Org Owner) */}
      {(() => {
        const isOrgOwnedAssistant =
          !!(baseAssistant as any)?.owner_organization_id &&
          instanceData?.organization_id ===
            (baseAssistant as any).owner_organization_id;
        if (!(isAdmin || isOrgOwnedAssistant)) return null;
        return (
          <Sheet
            open={showSuperAdminModal}
            onOpenChange={setShowSuperAdminModal}
          >
            <SheetContent className="w-[60%] max-w-none p-0 overflow-hidden">
              <div className="flex flex-col h-full">
                <SheetHeader className="p-6 pb-0">
                  <SheetTitle>Edit AI Tool</SheetTitle>
                  <div id="assistant-tabs-portal" className="mt-2" />
                </SheetHeader>
                <div className="flex-1 overflow-y-auto p-6">
                  {baseAssistant && (
                    <AIAssistantForm
                      initialData={baseAssistant}
                      onSubmit={(data) =>
                        updateSystemAssistantMutation.mutate(data)
                      }
                      onCancel={() => setShowSuperAdminModal(false)}
                      isLoading={updateSystemAssistantMutation.isPending}
                    />
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        );
      })()}

      {/* Regenerate Modal */}
      <RegenerateModal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        onRegenerate={(fb?: string) => {
          if (activeTab === "generate") {
            handleRegenerate(fb);
          } else {
            handleRegenerateResults(fb);
          }
        }}
        isGenerating={
          activeTab === "generate" ? isGenerating : isResultsImproving
        }
      />
    </MainLayout>
  );
};

export default AIAssistantDetail;
